import {
  Modal,
  OverflowTooltip,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IIndividualBeneficiaryRefundListData } from "../../../../models/individual-beneficiary-refund";
import {
  individualBeneficiaryRefundService,
  retryRefundIntegration,
} from "../../../../services/individualBeneficiaryRefund.service";
import MacroFilter, { IFilterParams } from "./MacroFilter";
import RefundDetailsModal from "./RefundDetailsModal";

const translationPath = "page.4uBus.logs.refund.";

const ERRO_CODE = "4";
const ERRO_EMAILENVIADO_CODE = "5";

const statusColors: Record<
  string,
  "warning" | "info" | "success" | "error" | "default"
> = {
  "1": "warning", // Pendente
  "2": "info", // Processando
  "3": "success", // Processado
  "4": "error", // Erro
  "5": "error", // Erro_EmailEnviado
  "6": "warning", // RetentativaManual
};

const Refund = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<IIndividualBeneficiaryRefundListData | null>(null);

  const { setMessage } = useNotificationContext();
  const { sendRequest: retryRequest } = useFetch(retryRefundIntegration);

  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    HolderDocument: "",
    StatusCode: "",
    CreatedStart: today,
    CreatedEnd: today,
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const formatDate = useCallback(
    (value: unknown) => {
      if (!value) return "-";
      const date = new Date(value as string);
      if (isNaN(date.getTime())) return "-";
      return format(date, "Pp", {
        locale: language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
      }).replaceAll(",", "");
    },
    [language],
  );

  const columns: GridColDef<IIndividualBeneficiaryRefundListData>[] = [
    {
      field: "holderDocument",
      headerName:
        t(translationPath + "holder_document") || "Documento do Titular",
      flex: 1,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "holderBankAccountName",
      headerName:
        t(translationPath + "holder_bank_account_name") || "Nome do Titular",
      flex: 1,
      renderCell: (params) => {
        const name = params.value ?? "-";
        return (
          <OverflowTooltip
            text={name}
            tooltip={name}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "integrationStatus",
      headerName: t(translationPath + "status") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <Chip
            label={params.value.name}
            color={statusColors[params.value.code] ?? "default"}
            size="small"
          />
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      flex: 1,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "integratedAt",
      headerName: t(translationPath + "integrated_at") || "Data de Integração",
      flex: 1,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "attemptCount",
      headerName: t(translationPath + "attempt_count") || "Tentativas",
      flex: 0.5,
      renderCell: (params) => params.value ?? 0,
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      flex: 1,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "protocolIntegrationId",
      headerName: t(translationPath + "protocol_integration_id") || "Protocolo",
      flex: 0.7,
      renderCell: (params) => params.value ?? "-",
    },
  ];

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleRetry = useCallback(
    async (id: string) => {
      const { success } = await retryRequest(id);

      if (success) {
        setRefresh((prev) => prev + 1);
        setMessage({
          message: t(translationPath + "retry_success"),
          type: "success",
        });
      }
    },
    [retryRequest, setMessage, t],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <RefundDetailsModal
            refundData={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>
      {}
      <Box
        sx={{
          height: "100%",
          "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
            display: "none !important",
          },
        }}
      >
        <Table<IIndividualBeneficiaryRefundListData>
          title={t(translationPath + "title")}
          columns={columns}
          service={individualBeneficiaryRefundService}
          searchInputPlaceHolder={
            t(translationPath + "search_placeholder") || "Search..."
          }
          showDefaultMacroFilters={false}
          showButton={false}
          showDeleteButton={false}
          defaultActions={true}
          getRowId={(row) => row.id}
          rowActions={(params) => [
            {
              action: () => {
                setSelectedRow(params.row);
                setModalOpen(true);
              },
              icon: <VisibilityIcon fontSize="small" />,
              text: t(translationPath + "view_details") || "Ver detalhes",
            },
            params.row.integrationStatus?.code === ERRO_CODE ||
            params.row.integrationStatus?.code === ERRO_EMAILENVIADO_CODE
              ? {
                  action: () => handleRetry(params.row.id),
                  icon: <ReplayIcon fontSize="small" />,
                  text: t(translationPath + "retry") || "Tentar novamente",
                }
              : null,
          ]}
          serviceProps={useMemo(
            () => ({
              refresh,
              ...filterParams,
              CreatedStart: filterParams?.CreatedStart
                ? new Date(filterParams.CreatedStart.setHours(15))
                    .toISOString()
                    .substring(0, 10)
                : undefined,
              CreatedEnd: filterParams?.CreatedEnd
                ? new Date(filterParams.CreatedEnd.setHours(15))
                    .toISOString()
                    .substring(0, 10)
                : undefined,
            }),
            [refresh, filterParams],
          )}
          macroFilters={useCallback(
            () => (
              <MacroFilter
                filterParams={filterParams}
                onFilterParams={handleFilterChange}
              />
            ),
            [filterParams, handleFilterChange],
          )}
        />
      </Box>
    </>
  );
};

export default Refund;
